<template>
  <section
    id="courses"
    class="shop-area text-left pt-60 pb-90 p-relative"
  >
    <div class="container">
      <div class="row align-items-center class-scroll">
        <div class="about-title second-title pb-25">
          <!-- <h5>Our Courses</h5> -->
          <h2 style="color:#2C5C9E">Latest Courses at Archvarsity</h2>
        </div>

        <!-- No Courses Message -->
        <div v-if="courses.length === 0" class="col-lg-12 text-center">
          <p style="color:#2C5C9E">No courses available at the moment.</p>
        </div>

        <!-- Course Cards -->
        <div
          v-for="course in courses"
          :key="course._id"
          class="col-lg-4 col-md-6"
        >
          <div class="class-item mb-30">
            <div class="class-img">
              <div class="class-img-outer">
                <a :href="`/live-course-details/${course._id}`">
                  <div
                    class="course-thumbnail"
                    :style="{ backgroundImage: `url(${course.coverImage || defaultThumbnail})` }"
                  ></div>
                </a>
                <div class="course-meta">
                  <div class="row align-items-center">
                    <div class="col-lg-8">
                      <div class="author">
                        <div class="thumb">
                          <div
                            :style="{
                              backgroundImage: course.facilitators[0]?.profileImage 
                                ? `url(${course.facilitators[0].profileImage})` 
                                : `url(${defaultAvatar})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              height: '50px',
                              width: '50px',
                              borderRadius: '50%',
                              border: '2px solid #fff',
                            }"
                          ></div>
                        </div>
                        <div class="text">
                          <a>{{ course.facilitators[0]?.firstName || 'Instructor' }}</a>
                          <p>{{ course.facilitators[0]?.title || 'Expert' }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Course Metadata -->
            <ul class="schedule">
              <li>
                <span>Category</span>
                <span class="class-size">{{ course.category || 'General' }}</span>
              </li>
              <li>
                <span>Duration</span>
                <span class="class-size">{{ course.duration || 'Flexible' }}</span>
              </li>
              <li>
                <span>Total Spots</span>
                <span class="class-size">{{ course.capacity || 'Unlimited' }}</span>
              </li>
            </ul>

            <!-- Course Content -->
            <div class="class-content">
              <h4 class="title">{{ course.title }}</h4>
              <p class="mb-20">{{ course.shortSummary || 'Explore this course to learn more.' }}</p>
              <a :href="`/live-course-details/${course._id}`" class="readmore">
                View Course Details <i class="fal fa-long-arrow-right"></i>
              </a>
            </div>
          </div>
        </div>

        <!-- "More Courses" Card (Fixed Size) -->
        <div v-if="currentPage === totalPages" class="col-lg-4 col-md-6">
          <div class="class-item mb-30">
            <div class="class-img">
              <div class="class-img-outer">
                <a>
                  <img 
                    src="@/assets/img/class/more.png" 
                    alt="More courses" 
                    class="course-thumbnail"
                  />
                </a>
              </div>
            </div>
            <div class="class-content">
              <h4 class="title">Looking for more courses?</h4>
              <p>
                Suggest a course you'd like us to offer! We prioritize student requests 
                and collaborate with experts to create industry-standard content.
              </p>
              <a href="mailto:info@archvarsity.com" class="readmore">
                Contact Us <i class="fal fa-long-arrow-right"></i>
              </a>
            </div>
          </div>
        </div>

        <!-- pagination container -->
        <div v-if="totalPages > 1" class="row">
          <div class="col-lg-12">
            <div class="pagination-container text-center mt-30">
              <button 
                @click="prevPage" 
                :disabled="currentPage === 1"
                class="pagination-button"
              >
                <i class="fal fa-angle-left"></i> Previous
              </button>
              
              <button 
                v-for="page in visiblePages" 
                :key="page"
                @click="goToPage(page)"
                :class="{ active: currentPage === page }"
                class="pagination-button"
              >
                {{ page }}
              </button>
              
              <button 
                @click="nextPage" 
                :disabled="currentPage === totalPages"
                class="pagination-button"
              >
                Next <i class="fal fa-angle-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'CoursesPage',
  data() {
    return {
      courses: [],
      defaultThumbnail: null,
      defaultAvatar: null,
      currentPage: 1,
      itemsPerPage: 9,
      totalItems: 0,
      totalPages: 1,
      visiblePageRange: 5 // Number of page buttons to show
    };
  },
  computed: {
    visiblePages() {
      const range = [];
      const halfRange = Math.floor(this.visiblePageRange / 2);
      let start = Math.max(1, this.currentPage - halfRange);
      let end = Math.min(this.totalPages, start + this.visiblePageRange - 1);

      // Adjust if we're at the end
      if (end - start + 1 < this.visiblePageRange) {
        start = Math.max(1, end - this.visiblePageRange + 1);
      }

      for (let i = start; i <= end; i++) {
        range.push(i);
      }
      return range;
    }
  },
  watch: {
    '$route.query': {
      immediate: true,
      handler(newVal) {
        this.currentPage = parseInt(newVal.page) || 1;
        this.fetchCourses();
      }
    }
  },
  methods: {
    async fetchCourses() {
      try {
        const response = await api.get("/api/courses/public", {
          params: {
            page: this.currentPage,
            limit: this.itemsPerPage
          }
        });
        
        this.courses = response.data.courses // Adjust based on your API response
        this.totalItems = response.data.totalCount || 0;
        this.totalPages = Math.ceil(this.totalItems / this.itemsPerPage);
      } catch (error) {
        console.error("Error fetching courses:", error);
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.goToPage(this.currentPage - 1);
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.goToPage(this.currentPage + 1);
      }
    },
    goToPage(page) {
      if (page !== this.currentPage) {
        this.$router.push({
          query: { ...this.$route.query, page }
        });
      }
    }
  }
};
</script>

<style scoped>
/* Ensure consistent card height */
/* Parent container for cards */
.class-item {
  height: 100%; /* Takes full height of grid cell */
  display: flex;
  flex-direction: column;
  border: 1px solid #eee;
  border-radius: 8px;
  overflow: hidden; /* Prevents content overflow */
}

/* Fix thumbnail height (16:9 ratio) */
.class-img-outer {
  position: relative;
  padding-top: 56.25%; /* 16:9 aspect ratio */
}

.course-thumbnail {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover; /* Ensures images fill space without stretching */
}

/* Set fixed height for the text/content section */
.class-content {
  padding: 20px;
  flex: 1; /* Expands to fill remaining space */
  min-height: 220px; /* Adjust as needed */
  display: flex;
  flex-direction: column;
}

/* Ensure course title and summary don't overflow */
.class-content h4 {
  font-size: 18px;
  margin-bottom: 10px;
  display: -webkit-box;
  -webkit-line-clamp: 2; /* Limit to 2 lines */
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.class-content p {
  flex-grow: 1; /* Pushes button to bottom */
  display: -webkit-box;
  -webkit-line-clamp: 3; /* Limit to 3 lines */
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.class-item:hover {
  transform: translateY(-5px);
}

.schedule {
  padding: 10px 15px;
  background: #f9f9f9;
  margin: 0;
  list-style: none;
  flex-shrink: 0;
  width: 100%; /* Ensure full width */
  box-sizing: border-box; /* Include padding in width calculation */
}

.schedule li {
  display: flex;
  justify-content: space-between;
  padding: 5px 0;
  align-items: center; /* Vertically center items */
  min-height: 25px; /* Minimum height for each row */
}

.schedule span {
  display: inline-block;
  font-size: 14px;
}

.schedule span:first-child {
  font-weight: 600;
  color: #555;
  min-width: 40%; /* Ensure first column has consistent width */
}

.schedule .class-size {
  text-align: right;
  color: #2C5C9E; /* Your blue color */
  min-width: 50%; /* Ensure second column has space */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.readmore {
  color: #2C5C9E; /* Your blue shade */
  font-weight: 600;
}

.readmore:hover {
  color: #1A4B8C; /* Darker blue on hover */
}
.pagination-container {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 30px;
}

.pagination-button {
  padding: 8px 16px;
  margin: 0 5px;
  border: 1px solid #ddd;
  background-color: #fff;
  color: #2C5C9E;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  min-width: 40px;
  text-align: center;
}

.pagination-button:hover:not(:disabled) {
  background-color: #2C5C9E;
  color: white;
  border-color: #2C5C9E;
}

.pagination-button:disabled {
  color: #ccc;
  cursor: not-allowed;
  opacity: 0.7;
}

.pagination-button.active {
  background-color: #2C5C9E;
  color: white;
  border-color: #2C5C9E;
  font-weight: bold;
}

@media (max-width: 576px) {
  .pagination-container {
    gap: 5px;
  }
  .pagination-button {
    padding: 6px 12px;
    margin: 0 2px;
    min-width: 30px;
  }
}
</style>
